import type { MemoryDictionary } from '../memoryContract'

const memory = {
  page: {
    eyebrow: '企業マーケティング記憶',
    title: 'マーケティング記憶',
    intro: 'ここは資料置き場ではありません。Navigator が現在この企業をどう理解しているかを示し、診断・実行・公開・振り返りを同じ認識から続けられるようにします。',
    updated: '企業記憶を更新しました',
    lastUpdated: '最終更新 · {date}',
    reportIssue: '認識に誤りがある',
  },
  tabs: {
    overview: '現在の理解',
    cognition: '企業理解',
    knowledge: '知っていること',
    strategy: '進め方',
    identity: 'らしさ',
    connections: 'つながり',
  },
  cognition: {
    title: 'Navigator がこの企業をどう理解したか', body: '確認済みの初期プロファイルと企業調査をいつでも見直せます。一度きりの導入画面ではなく、プロジェクトに残る認知成果です。', assets: '件の認知成果', loading: '企業理解を読み込み中', loadingBody: 'このプロジェクトの初期プロファイルと企業調査を取得しています。', error: '企業理解を一時的に読み込めません', errorBody: '他の企業記憶は変わっていません。再度読み込んでください。', profileEyebrow: '初期プロファイル', profileTitle: '確認済み企業プロファイル', researchEyebrow: '企業調査', researchTitle: '調査統合結果', notCreated: '未作成', completed: '完了', confirmedAt: '確認日 · {date}', generatedAt: '生成日 · {date}', profileEmpty: '確認済みの初期プロファイルはありません。', researchEmpty: '企業調査はまだ完了していません。', summary: '調査結論', facts: '事実 {count} 件', hypotheses: '仮説 {count} 件', unknowns: '不明点 {count} 件', candidates: '候補 {count} 件', findings: '重要な発見 {count} 件を開く', evidence: '事実・仮説・不明点を確認', knownFacts: '既知の事実', hypothesisTitle: '検証待ちの仮説', unknownTitle: '不明点と矛盾', fields: { brand_identity: 'ブランド情報', domain_footprint: '公式サイトとドメイン', product_lines: '主な製品・製品ライン', markets_languages: '対象市場と言語', buyer_types: '典型的な顧客・買い手', competitors: '初期競合候補', public_content_foundation: '公開コンテンツ基盤', website_shape: '公式サイトの状態', company_type: '初期企業タイプ' },
  },
  common: {
    confirmed: '確認済み',
    awaitingConfirmation: '確認待ち',
    awaitingEvidence: '追加の根拠が必要',
    continueObserving: '観察を継続',
    inProgress: '進行中',
    connected: '接続済み',
    view: '表示',
    close: '閉じる',
    cancel: 'キャンセル',
    retry: '再試行',
    later: '後で対応',
  },
  contentLanguage: {
    original: '原文 · {language}',
    unchangedHint: '企業の事実、ブランド原文、証拠の引用は元の言語のまま表示されます。',
  },
  overview: {
    currentUnderstanding: 'Navigator による現在の {company} 理解 · 確認待ち',
    viewBrandDirection: 'ブランド方針を確認',
    suggestCorrection: '修正を提案',
    sharedUnderstanding: 'すべてのマーケティング施策を同じ理解から続ける',
    map: {
      knowledge: '知っていること',
      knowledgeHint: '事実と市場',
      strategy: '進め方',
      strategyHint: '戦略と実行',
      identity: 'らしさ',
      identityHint: '価値と選択',
      connections: 'つながり',
      connectionsHint: '行動と結果',
    },
    cards: {
      knowledgeTitle: '知っていること',
      knowledgeBody: '確認済みの事実、市場の反応、まだ補うべき理解を確認します。',
      strategyTitle: '進め方',
      strategyBody: 'この段階のマーケティング方針、実行状況、次の提案を確認します。',
      identityTitle: 'らしさ',
      identityBody: '市場に覚えてほしい中心メッセージ、価値の柱、各モジュールの役割を確認します。',
      connectionsTitle: 'つながり',
      connectionsBody: '各成果がどこから生まれ、何を変え、次の一手にどうつながったかを確認します。',
      enter: '開く →',
      confirmedModules: '{count} 件確認済み',
      liveOutcomes: '{count} 件の成果',
    },
    learning: {
      eyebrow: '最近の学習',
      title: '最近学んだこと',
      body: 'Navigator は本当に効いた表現と、まだ検証が必要な方向を記憶し、次の仕事を今回の結果から始めます。',
      adopted: '使用中',
      observing: '検証を継続',
    },
  },
  knowledge: {
    eyebrow: '知っていること',
    title: '確認できたことと、まだ知るべきこと',
    body: '製品の事実、市場の反応、まだ不足している情報を確認し、必要に応じて Navigator の理解を修正できます。',
    confirmedFacts: '公開事実 {count} 件を確認済み',
    search: '事実、市場の反応、不足情報を検索',
    filter: {
      all: 'すべて',
      facts: '確認済みの事実',
      market: '市場の反応',
      gaps: '不足情報',
      outcomes: '成果',
    },
    addMaterial: '資料を追加',
    category: {
      positioning: '製品ポジショニング',
      specification: '製品仕様',
      platforms: '対応環境とバージョン',
      marketSignal: '市場の反応',
      gap: '不足情報',
      outcome: '成果',
    },
    source: {
      official: '公式サイト',
      aiMonitoring: 'AI 回答モニタリング',
      awaitingSamples: '追加サンプル待ち',
      humanReview: '人による評価',
    },
    action: {
      viewBasis: '根拠を見る',
      viewFacts: '元の事実 {count} 件を見る',
      viewAnswer: 'この回答を見る',
      viewMissing: '不足しているものを見る',
      viewLearning: '学んだ内容を見る',
    },
    status: {
      confirmed: '確認済み',
      moreEvidence: '追加の根拠が必要',
      passedReviews: '{count} 回の評価を通過',
    },
    noMatchTitle: '一致する記憶が見つかりません',
    noMatchBody: '別のキーワードを試すか、すべての種類を表示してください。',
  },
  strategy: {
    eyebrow: 'この段階の計画',
    title: 'この段階のマーケティングをどう進めるか',
    body: '現在の目標、進行中の仕事、あなたの参加が必要な項目、次の提案をまとめています。',
    responsibilitySummary: 'Navigator が担当 {owned} 件 / 共同対応 {collaborative} 件',
    directionAwaiting: 'この段階のマーケティング方針 · 確認待ち',
    emptyTitle: 'この段階の方針はまだ確定していません',
    emptyBody: 'Navigator は実行記録を戦略として扱いません。まず段階目標と方針を確認してください。',
    workEmptyTitle: 'Navigator が進める作業はまだ予定されていません',
    workEmptyBody: '戦略が確認され、実行計画になった後にここへ表示されます。',
    guidanceEmptyTitle: 'チーム向けの提案はまだありません',
    guidanceEmptyBody: 'Navigator の実行範囲外の作業が必要な場合にのみ表示されます。',
    brief: {
      goal: 'この段階で達成すること',
      message: '市場に覚えてほしいこと',
      reviewWhen: '方針を見直す時期',
    },
    journey: {
      goal: '目標を定める',
      goalHint: '誰に、どんな変化を起こすか',
      theme: 'テーマを決める',
      themeHint: 'すべての発信に共通の焦点を持たせる',
      work: '仕事を進める',
      workHint: 'コンテンツ、ページ、公開、連携',
      feedback: '反応を読む',
      feedbackHint: 'AI 回答、リード、顧客の声',
      next: '次を決める',
      nextHint: '継続、調整、または方向転換',
    },
    navigatorLed: {
      eyebrow: 'Navigator が担当',
      title: 'Navigator が実行します',
      body: '以下の仕事は、方針整理から公開、振り返りまで Navigator が継続して進めます。',
      count: '{count} 件の仕事',
      positioningTitle: 'この段階の主張と競争上の位置を定める',
      positioningBody: '買い手が本当に気にする問題から始め、中心価値、モジュールの役割、選ぶ理由を明確にします。',
      contentTitle: '戦略を公開できるコンテンツにする',
      contentBody: '製品ページ、FAQ、購入ガイド、比較コンテンツ、多言語ページを継続して制作します。',
      machineTitle: 'サイトと AI の両方に新しい内容を理解させる',
      machineBody: '既存サイトに入口、ページ間の関係、機械可読情報を追加します。',
      publishTitle: '公開し、すべての成果が正しく見えることを確認する',
      publishBody: '許可された範囲で手動、共同、自動公開を使い分け、ページと検索入口を確認します。',
      reviewTitle: '市場の反応を追い、次の段階を準備する',
      reviewBody: 'AI 回答、リード、顧客の声から、続ける方向と調整する方向を判断します。',
      completed: '完了',
      pagesOnline: '{count} ページ公開中',
      ongoing: '継続更新',
      connected: '接続済み',
      monitoring: '観察中',
    },
    collaboration: {
      eyebrow: 'あなたのチームと連携',
      title: 'あなた、またはチームの参加が必要です',
      body: 'Navigator が方向とコンテンツを準備します。開始時期はあなたが決め、担当チームが実行します。',
      socialTitle: '{channels} での発信',
      socialBody: 'Navigator がテーマ、頻度、展開案を準備し、あなたが確認して公開します。',
      socialStatus: '共同対応',
      mediaTitle: '有料メディア',
      mediaBody: 'Navigator が対象、論点、素材を提案し、メディア担当が予算と配信を管理します。',
      mediaStatus: 'アカウントが必要',
      prTitle: 'PR・イベント・パートナーシップ',
      prBody: 'Navigator が話題、時期、協業先の方向を提案し、あなたや担当チームが対外連絡を行います。',
      prStatus: 'チーム対応',
    },
    results: {
      eyebrow: '市場の反応',
      title: '次の一手は、実際の反応から決める',
      body: 'Navigator は、市場がどう語るか、どんな顧客が近づいているか、なぜ選ぶのか・迷うのかを同時に見ます。',
      aiTitle: 'AI はあなたをどう語っているか',
      aiBody: 'ブランドが言及されているか、どう推薦されるか、どの競合と並ぶか、何が引用されるかを追跡します。',
      aiAction: '継続監視 · 最新評価を見る →',
      leadsTitle: 'どんな顧客が近づいているか',
      leadsBody: '流入元、買い手の質問、その後の進捗から、マーケティングが適切な相手を呼び込めているか判断します。',
      leadsAction: 'リード接続済み · 変化を見る →',
      surveyTitle: '顧客はなぜ選び、なぜ迷うのか',
      surveyBody: 'どの表現が信頼につながったか、何が不明なままか、最終判断に何が影響したかを直接聞きます。',
      surveyAction: 'いつでも実施可能 · アンケートを作成 →',
    },
    recommendation: {
      continue: '継続を推奨',
      adjust: '調整を推奨',
      schedule: '日程調整待ち',
    },
    methods: {
      eyebrow: 'Navigator が学んだこと',
      title: 'Navigator が身につけた仕事の原則',
      body: '実際の仕事と人の判断から得た原則が、次の戦略とコンテンツに反映されます。',
      count: '{active} 件使用中 / {observing} 件検証中',
      search: '原則を検索',
      filterAll: 'すべて',
      filterActive: '使用中',
      filterObserving: '検証中',
      expression: '表現の原則',
      competition: '競争の原則',
      promise: '約束の原則',
      inUse: '使用中',
      validating: '検証中',
      impact: '仕事への影響を見る',
      scope: '適用範囲を見る',
      validation: '残る検証項目を見る',
      emptyBody: 'ここに表示できる十分な根拠を持つ作業原則はまだありません。',
    },
  },
  identity: {
    eyebrow: '市場にどう覚えてほしいか',
    title: '{company} はどう理解されたいか',
    body: '市場に最初に覚えてほしいことと、企業の製品や能力がその理解をどう支えるかを確認します。',
    confirmedModules: 'モジュール表現 {count} 件を確認済み',
    coreAwaiting: '中心メッセージ · 確認待ち',
    confirm: 'この表現を確認',
    revise: '修正を提案',
    usage: '使用先を見る',
    awaitingDecision: 'あなたの判断待ち',
    notActive: '中心メッセージはまだ有効ではありません',
    afterConfirm: '確認後、Navigator がサイト、コンテンツ、今後のマーケティング施策に一貫して使用します。',
    pillarAwaiting: '確認してください',
    modulesEyebrow: '確認済みのモジュール表現',
    modulesTitle: '製品と能力が中心価値をどう支えるか',
    modulesCount: '{count} 件確認済み',
    moduleAction: '確認済み · 全文を見る',
    noCoreTitle: 'コアメッセージはまだ形成されていません',
    noCoreBody: '確認済みの企業資料と人の意思を基に、Navigator がコアメッセージを提案します。',
    noModulesBody: '確認済みの製品・機能メッセージがここに表示されます。',
  },
  connections: {
    eyebrow: '次の段階へ',
    title: 'すべての行動が、次の精度を高める',
    body: '前の段階で何を行い、市場がどう反応し、その結果が次の一手をどう変えたかを確認できます。',
    onlineOutcomes: '{count} 件公開中',
    journeyEyebrow: '連続する一つの流れ',
    journeyTitle: '判断から実行へ、結果から次の段階へ',
    steps: {
      stage: '段階を把握',
      stageHint: '事業目標、買い手の質問、認知の差',
      strategy: '戦略を定める',
      strategyHint: '段階テーマ、競争上の位置、成功条件',
      work: '仕事を進める',
      workHint: 'コンテンツ、ページ、公開、連携を組み立てる',
      results: '結果を読む',
      resultsHint: 'AI 回答、リード、顧客アンケート',
      next: '次の段階へ',
      nextHint: '継続、調整、方向転換を決め、学びを残す',
    },
    note: 'Navigator は AI 回答、リード、顧客の声をまとめて判断します。効果のあった方向は続け、効果のなかった試みも記憶して、同じ遠回りを避けます。',
    recentTitle: '直近の完了したアクション',
    recentBody: '買い手の質問から二言語ガイドを公開し、その結果を次のコンテンツへ反映するまで。',
    noRecentBody: '完全なアクション履歴はまだ記録されていません。',
    viewProcess: '全体の流れを見る',
    accumulatedTitle: 'Navigator が積み上げたもの',
    accumulatedBody: 'これらの記録により、次の仕事をゼロからではなく既存の経験から始められます。',
    stats: {
      live: '公開中の成果',
      attempts: '記録済みの試行',
      judgments: '経験からの判断',
      actions: '完了したアクション',
    },
  },
  airlockGap: {
    eyebrow: 'Airlock 公開から',
    title: 'Airlock の回答に必要な企業根拠を補う',
    subject: '補足する項目',
    status: '現在の状態',
    statusPending: '資料の追加待ち',
    statusQueued: '資料を送信済み · 処理待ち',
    statusSynced: '同期済み · 再テストできます',
    flowLabel: '知識補完フロー',
    flow: {
      supply: '事実を追加',
      classify: '資料を分類',
      confirm: '知識を確認',
      retest: '戻って再テスト',
    },
    reason: {
      missing_knowledge: '承認済みの企業資料には、この項目への確かな回答がまだありません。原典または正確な事業上の事実を追加してください。',
      conflicting_knowledge: '既存資料の記述が一致していません。現在有効な正式見解と、置き換える内容を追加してください。',
      knowledge_not_public: '社内情報はありますが、Airlock で公開利用できません。公開可能な表現を追加するか、安全な境界を選ぶために戻ってください。',
      knowledge_not_synced: '確認済み知識がこの Airlock の利用範囲にまだ同期されていません。既存知識を確認するか、不足する根拠を追加してください。',
      not_applicable_review: 'この項目には事業上の判断が必要です。該当する場合は事実を追加し、該当しない場合は Airlock に戻って確認してください。',
    },
    queuedBody: '追加内容は資料処理キューに入りました。確認済み知識となり、この Airlock に同期されるまで公開上の不足は閉じません。',
    addStatement: '事実を追加',
    submitted: '事実を送信済み',
    reviewKnowledge: '既存知識を見る',
    returnToAirlock: 'Airlock 公開へ戻る',
    correctionTitle: 'この Airlock の不足に根拠を追加',
    correctionBody: '検証可能な事業上の事実と、可能なら出典を記入してください。送信しても即時公開や自動解消はされません。',
    correctionLabel: '事業上の事実または根拠',
    correctionPlaceholder: '例：標準仕様の最低注文数は 500 個。出典は 2026 年の現行販売方針です…',
    correctionSubmit: '処理に送信',
    queuedToast: '資料を受け付けました。知識の確認と同期が完了するまで Airlock の不足は開いたままです。',
  },
  states: {
    loadingTitle: '企業記憶をまとめています',
    loadingBody: 'Navigator が事実、戦略、実行、結果、学びを読み込んでいます。未取得の情報を 0 や「存在しない」として扱うことはありません。',
    emptyEyebrow: '新しい企業',
    emptyTitle: 'この企業の記憶はまだ始まっていません',
    emptyBody: 'まず公式サイトと資料を接続してください。Navigator が下調べを行い、あなたの判断が必要な部分だけを持ち帰ります。',
    connectMaterial: '最初の資料を接続',
    readWebsite: '先にサイトを読む',
    errorEyebrow: '読み込み失敗',
    errorTitle: '企業記憶を一時的に読み込めません',
    errorBody: '既存の記憶は失われず、空の状態で上書きされません。再試行するか、前回の信頼できるスナップショットを確認してください。',
    retry: '再読み込み',
    lastSnapshot: '前回のスナップショットを見る',
    snapshotUnavailable: 'このセッションには利用可能な信頼済みスナップショットがありません。',
    humanEyebrow: 'あなたの判断が必要です',
    humanTitle: '成立する方向が複数見つかりました',
    humanBody: 'Navigator は代わりに選びません。選択肢を比較し、企業の意志に最も合う表現を確認してください。',
    choose: '選択肢を見る',
    later: '後で対応',
    decisionDeferred: '判断は保留のままで、方針は変更されていません。',
  },
  detail: {
    eyebrow: '記憶の詳細',
    correct: 'この内容を修正',
    source: '出典を見る',
    correctionTitle: 'Navigator の理解を修正',
    correctionBody: '修正内容は新しい入力として扱われます。確認済みの事実や元の出典を黙って上書きすることはありません。',
    correctionLabel: '修正内容',
    correctionPlaceholder: '例：買い手がまず私たちを選ぶ理由は長期的に管理できることであり、単に一つの機能が便利だからではありません…',
    cancel: 'キャンセル',
    submit: '修正を送信',
    submitting: '送信中…',
    correctionQueued: '修正を受け付けました。分域後に企業記憶へ反映されます。',
    correctionFailed: '修正を送信できませんでした。',
    fields: {
      id: '記録 ID',
      locale: '原文の言語',
      source: '出典',
      scope: '適用範囲',
      confidence: '信頼度',
      status: '状態',
      question: '買い手の質問',
      platform: 'プラットフォーム',
      output: '関連成果',
    },
  },
} satisfies MemoryDictionary

export default memory
